/**
 * GuidedFlowPanel - Step-by-step wizard for setup tasks
 * P1: Users/RBAC Setup and Upload Take-on flows
 */

import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, Circle, ChevronRight, ChevronDown, Users, Upload, 
  Shield, Settings, ExternalLink, Loader2, AlertCircle, Check,
  ArrowRight, FileSpreadsheet, RefreshCw
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Flow type configurations
const FLOW_CONFIGS = {
  rbac: {
    id: 'rbac',
    title: 'Users & RBAC Setup',
    icon: Users,
    description: 'Set up users, assign roles, and configure access groups',
    color: 'blue'
  },
  upload: {
    id: 'upload',
    title: 'Upload Take-on',
    icon: Upload,
    description: 'Validate and import data from Excel templates',
    color: 'green'
  }
};

// Step component
const FlowStep = ({ step, index, isActive, isCompleted, onToggle, onValidate }) => {
  const [expanded, setExpanded] = useState(isActive);
  
  return (
    <div className={`border rounded-lg ${isActive ? 'border-purple-300 bg-purple-50/50' : 'border-gray-200'}`}>
      {/* Step Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3 text-left"
      >
        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
          isCompleted 
            ? 'bg-green-500 text-white' 
            : isActive 
              ? 'bg-purple-500 text-white' 
              : 'bg-gray-200 text-gray-500'
        }`}>
          {isCompleted ? (
            <Check className="w-3.5 h-3.5" />
          ) : (
            <span className="text-xs font-medium">{index + 1}</span>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-medium ${isCompleted ? 'text-green-700' : isActive ? 'text-purple-700' : 'text-gray-700'}`}>
            {step.title}
          </h4>
        </div>
        
        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      
      {/* Step Content */}
      {expanded && (
        <div className="px-3 pb-3 pl-12">
          <div className="text-sm text-gray-600 prose prose-sm max-w-none">
            <div dangerouslySetInnerHTML={{ __html: step.content.replace(/\n/g, '<br/>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
          </div>
          
          {/* Action buttons */}
          <div className="mt-3 flex items-center gap-2">
            {!isCompleted && (
              <button
                onClick={() => onToggle(index)}
                className="text-xs px-2.5 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Mark Complete
              </button>
            )}
            {isCompleted && (
              <button
                onClick={() => onToggle(index)}
                className="text-xs px-2.5 py-1 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Mark Incomplete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Validator status component
const ValidatorStatus = ({ validator, onRun }) => {
  const [status, setStatus] = useState('pending'); // pending, running, passed, failed
  const [result, setResult] = useState(null);
  
  const handleRun = async () => {
    setStatus('running');
    try {
      const res = await onRun(validator.type);
      setResult(res);
      setStatus(res.valid ? 'passed' : 'failed');
    } catch (err) {
      setStatus('failed');
      setResult({ message: err.message || 'Validation failed' });
    }
  };
  
  return (
    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2">
        {status === 'pending' && <Circle className="w-4 h-4 text-gray-300" />}
        {status === 'running' && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
        {status === 'passed' && <CheckCircle className="w-4 h-4 text-green-500" />}
        {status === 'failed' && <AlertCircle className="w-4 h-4 text-red-500" />}
        <span className="text-sm text-gray-700">{validator.label}</span>
      </div>
      
      <button
        onClick={handleRun}
        disabled={status === 'running'}
        className="text-xs px-2 py-1 text-purple-600 hover:bg-purple-100 rounded"
      >
        {status === 'running' ? 'Checking...' : 'Run Check'}
      </button>
    </div>
  );
};

// Main GuidedFlowPanel component
const GuidedFlowPanel = ({ flowType = 'rbac', onClose }) => {
  const { user } = useAuth();
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  
  const tenantId = user?.tenant?.id || user?.tenant_id;
  const config = FLOW_CONFIGS[flowType];
  
  useEffect(() => {
    loadFlow();
  }, [flowType, tenantId]);
  
  const loadFlow = async () => {
    if (!tenantId || !user?.id) return;
    
    setLoading(true);
    try {
      const res = await api.getGuidedFlow(flowType, tenantId, user.id);
      setFlow(res.data);
    } catch (err) {
      console.error('Failed to load guided flow:', err);
      toast.error('Failed to load setup guide');
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleStep = (index) => {
    const newCompleted = new Set(completedSteps);
    if (newCompleted.has(index)) {
      newCompleted.delete(index);
    } else {
      newCompleted.add(index);
      // Auto-advance to next step
      if (index < (flow?.steps?.length || 0) - 1) {
        setActiveStepIndex(index + 1);
      }
    }
    setCompletedSteps(newCompleted);
  };
  
  const runValidator = async (validatorType) => {
    if (validatorType === 'upload-template') {
      // Prompt for column headers (in real app, this would be from a file picker)
      const res = await api.validateUploadTemplate(tenantId, ['Name', 'Type', 'Parent Name', 'Status']);
      return res.data;
    }
    
    // For other validators, return mock result
    return { valid: true, message: 'Check passed' };
  };
  
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
      </div>
    );
  }
  
  if (!flow) {
    return (
      <div className="p-6 text-center text-gray-500">
        <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
        <p>Failed to load setup guide</p>
        <button onClick={loadFlow} className="mt-2 text-sm text-purple-600 hover:underline">
          Try again
        </button>
      </div>
    );
  }
  
  const completionPercent = Math.round((completedSteps.size / (flow.steps?.length || 1)) * 100);
  
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center gap-3 mb-3">
          {config && <config.icon className="w-6 h-6 text-purple-600" />}
          <div>
            <h3 className="font-semibold text-gray-900">{flow.title}</h3>
            <p className="text-xs text-gray-500">{flow.description}</p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-purple-500 transition-all duration-300"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
          <span className="text-xs font-medium text-gray-600">{completionPercent}%</span>
        </div>
      </div>
      
      {/* Steps */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {flow.steps?.map((step, index) => (
          <FlowStep
            key={index}
            step={step}
            index={index}
            isActive={activeStepIndex === index}
            isCompleted={completedSteps.has(index)}
            onToggle={handleToggleStep}
          />
        ))}
      </div>
      
      {/* Validators */}
      {flow.validators && flow.validators.length > 0 && (
        <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-gray-50">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Validation Checks</h4>
          <div className="space-y-2">
            {flow.validators.map((validator, idx) => (
              <ValidatorStatus
                key={idx}
                validator={validator}
                onRun={runValidator}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Quick Links */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          {flowType === 'rbac' && (
            <>
              <a href="/admin/users" className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                Open Users & Roles
              </a>
              <span className="text-gray-300">|</span>
              <a href="/admin/groups" className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                Manage Groups
              </a>
            </>
          )}
          {flowType === 'upload' && (
            <>
              <a href="/upload" className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                Open Upload Center
              </a>
              <span className="text-gray-300">|</span>
              <a href="/api/upload/template" className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                <FileSpreadsheet className="w-3 h-3" />
                Download Template
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuidedFlowPanel;
